// packages/web/src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { FirebaseService } from '@matchpoint/shared';
import { User, AuthService } from '@matchpoint/shared';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (email: string, password: string, name: string) => Promise<User>;
  signInWithGoogle: () => Promise<User>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const isMountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    console.log('🔍 AuthContext: useEffect triggered - initializing auth');
    isMountedRef.current = true;
    
    const initAuth = async () => {
      try {
        console.log('🔍 AuthContext: Starting Firebase initialization...');
        
        // Initialize Firebase once at app level
        await FirebaseService.getInstance().initializeApp();
        const auth = FirebaseService.getInstance().getAuth();
        const authService = AuthService.getInstance();

        console.log('✅ AuthContext: Firebase initialized, setting up auth listener');

        // IMPORTANT: Ensure persistence is set before setting up listener
        // This should already be done in firebaseWeb.ts, but we double-check here
        const { setPersistence, browserLocalPersistence } = await import('firebase/auth');
        try {
          await setPersistence(auth, browserLocalPersistence);
          console.log('✅ Auth persistence confirmed in AuthContext');
        } catch (persistError) {
          console.warn('⚠️ Persistence already set or failed:', persistError);
        }

        // Wait for Firebase Auth to fully initialize and restore session from persistence
        console.log('⏳ Waiting for auth state to be ready...');
        await auth.authStateReady();
        console.log('✅ Auth state is ready, auth.currentUser is now properly initialized');

        // Set up persistent auth listener
        unsubscribeRef.current = onAuthStateChanged(auth, async (firebaseUser) => {
          console.log('🔍 AuthContext: onAuthStateChanged triggered', {
            hasUser: !!firebaseUser,
            uid: firebaseUser?.uid,
            email: firebaseUser?.email
          });
          
          if (!isMountedRef.current) return;
          
          if (firebaseUser) {
            try {
              console.log('🔍 AuthContext: Firebase user exists, calling getCurrentUser');
              const userData = await authService.getCurrentUser();
              console.log('✅ AuthContext: getCurrentUser returned user:', {
                uid: userData?.uid,
                email: userData?.email
              });
              
              if (isMountedRef.current) {
                setUser(userData);
                console.log('✅ AuthContext: User state updated successfully');
              }
            } catch (error) {
              console.error('❌ AuthContext: Error getting current user:', error);
              if (isMountedRef.current) {
                setUser(null);
              }
            }
          } else {
            console.log('🔍 AuthContext: No Firebase user, setting user to null');
            if (isMountedRef.current) {
              setUser(null);
            }
          }
          
          if (isMountedRef.current) {
            setLoading(false);
            console.log('✅ AuthContext: Auth initialization complete, loading=false');
          }
        });
        
        console.log('✅ AuthContext: Auth listener established successfully');
      } catch (error) {
        console.error('❌ AuthContext: Error initializing auth:', error);
        if (isMountedRef.current) {
          setLoading(false);
          setUser(null);
          setAuthInitialized(true);
        }
      }
    };

    initAuth();

    return () => {
      console.log('🔍 AuthContext: Cleaning up - unmounting');
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const authService = AuthService.getInstance();
    const userData = await authService.login(email, password);
    setUser(userData);
    return userData;
  };

  const signUp = async (email: string, password: string, name: string) => {
    const authService = AuthService.getInstance();
    const userData = await authService.register(email, password, name);
    return userData;
  };

  const signInWithGoogle = async () => {
    console.log('🔍 AuthContext: signInWithGoogle called');
    const authService = AuthService.getInstance();
    const userData = await authService.signInWithGoogle();
    console.log('✅ AuthContext: signInWithGoogle completed, userData:', {
      uid: userData?.uid,
      email: userData?.email
    });
    
    // Don't manually setUser - let onAuthStateChanged handle it
    // But verify auth.currentUser is set
    const auth = FirebaseService.getInstance().getAuth();
    console.log('🔍 AuthContext: After signIn, auth.currentUser:', {
      hasCurrentUser: !!auth.currentUser,
      uid: auth.currentUser?.uid
    });
    
    // The onAuthStateChanged listener should trigger and update the user
    // Return the userData for compatibility
    return userData;
  };

  const signOut = async () => {
    const authService = AuthService.getInstance();
    await authService.signOut();
    setUser(null);
  };

  const refreshUser = async () => {
    const auth = FirebaseService.getInstance().getAuth();
    if (auth.currentUser) {
      const authService = AuthService.getInstance();
      try {
        const userData = await authService.getCurrentUser();
        if (isMountedRef.current) {
          setUser(userData);
        }
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    refreshUser,
  };

  // Always render children - let AppRoutes handle the loading screen
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};